import React from 'react';
import { OcrResult } from '../types';
import { formatDate, formatTime } from '../utils';
import { compareOcrResults, getDiffPathsForSide } from '../ocrComparison';
import { OcrPanel } from './OcrPanel';

interface RunCardProps {
  result: OcrResult;
  expanded: boolean;
  onToggle: () => void;
  onRemove?: () => void;
}

export function RunCard({ result, expanded, onToggle, onRemove }: RunCardProps) {
  const bothSucceeded = !result.documentAi.error && !result.deapOcr.error
    && result.documentAi.processed && result.deapOcr.raw;
  const comparison = bothSucceeded
    ? compareOcrResults(result.documentAi.processed, result.deapOcr.raw)
    : null;

  const gcpDiffPaths = comparison
    ? getDiffPathsForSide(comparison, 'gcp', result.documentAi.processed)
    : new Set<string>();
  const deapDiffPaths = comparison
    ? getDiffPathsForSide(comparison, 'deap', result.deapOcr.raw)
    : new Set<string>();

  const matchBadgeClass = comparison
    ? comparison.matchPercentage >= 90 ? 'high'
      : comparison.matchPercentage >= 70 ? 'medium'
      : 'low'
    : '';

  return (
    <div className="run-card">
      <div className="card-header" onClick={onToggle}>
        <div className="card-header-left">
          <svg
            className={`chevron ${expanded ? 'open' : ''}`}
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="#484F58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
          <span className="run-timestamp">{formatDate(result.timestamp)}</span>
        </div>
        <div className="card-actions" onClick={e => e.stopPropagation()}>
          {result.documentAi.error ? (
            <span className="run-timing-badge gcp failed">GCP Failed</span>
          ) : result.documentAi.totalTimeMs != null ? (
            <span className="run-timing-badge gcp">GCP {formatTime(result.documentAi.totalTimeMs)}</span>
          ) : null}
          {result.deapOcr.error ? (
            <span className="run-timing-badge deap failed">DEAP Failed</span>
          ) : result.deapOcr.processingTimeMs != null ? (
            <span className="run-timing-badge deap">DEAP {formatTime(result.deapOcr.processingTimeMs)}</span>
          ) : null}
          {comparison && (
            <span className={`match-badge ${matchBadgeClass}`}>
              Match {comparison.matchPercentage.toFixed(0)}%
            </span>
          )}
          {onRemove && (
            <button className="btn-trash" onClick={onRemove}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F85149" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="card-panels">
          <OcrPanel
            title="GCP Document AI"
            subtitle="Zpracovany vystup (extrahovane entity)"
            data={result.documentAi.error ? { error: result.documentAi.error } : result.documentAi.processed}
            rawData={result.documentAi.raw}
            error={result.documentAi.error}
            timings={[
              { label: result.documentAi.authCached ? 'Auth (cache)' : 'Auth', value: result.documentAi.authTimeMs, variant: result.documentAi.authCached ? 'auth-cached' : 'auth' },
              { label: 'Request', value: result.documentAi.requestTimeMs, variant: 'request' },
              { label: 'Parse', value: result.documentAi.parseTimeMs, variant: 'parse' },
              { label: 'Total', value: result.documentAi.totalTimeMs, variant: 'total' },
            ]}
            fileName={result.fileName}
            serviceName="documentai"
            diffPaths={gcpDiffPaths}
            missingPaths={comparison?.missingInGcp ?? new Set()}
            otherServiceName="DEAP OCR"
          />
          <OcrPanel
            title="DEAP OCR"
            subtitle="Surovy vystup z API"
            data={result.deapOcr.error ? { error: result.deapOcr.error } : result.deapOcr.raw}
            rawData={result.deapOcr.raw}
            error={result.deapOcr.error}
            timings={[
              { label: 'Total', value: result.deapOcr.processingTimeMs, variant: 'total' },
            ]}
            fileName={result.fileName}
            serviceName="deapocr"
            diffPaths={deapDiffPaths}
            missingPaths={comparison?.missingInDeap ?? new Set()}
            otherServiceName="GCP Document AI"
          />
        </div>
      )}
    </div>
  );
}
